/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getArrayDecoder,
  getArrayEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import {
  getFeeRoutingMatrixDecoder,
  getFeeRoutingMatrixEncoder,
  getRehypeBeneficiaryDecoder,
  getRehypeBeneficiaryEncoder,
  type FeeRoutingMatrix,
  type FeeRoutingMatrixArgs,
  type RehypeBeneficiary,
  type RehypeBeneficiaryArgs,
} from '../types';

export const REHYPE_STATE_DISCRIMINATOR = new Uint8Array([
  189, 101, 117, 1, 189, 167, 250, 225,
]);

export function getRehypeStateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REHYPE_STATE_DISCRIMINATOR,
  );
}

export type RehypeState = {
  discriminator: ReadonlyUint8Array;
  launch: Address;
  baseMint: Address;
  quoteMint: Address;
  hookProgram: Address;
  buybackDestination: Address;
  feeRouting: FeeRoutingMatrix;
  beneficiaries: Array<RehypeBeneficiary>;
  cumulativeBeneficiaryBase: bigint;
  cumulativeBeneficiaryQuote: bigint;
  distributedBaseByBeneficiary: Array<bigint>;
  distributedQuoteByBeneficiary: Array<bigint>;
  pendingCrossBase: bigint;
  pendingCrossQuote: bigint;
  pendingLpBase: bigint;
  pendingLpQuote: bigint;
  inflightAmountIn: bigint;
  inflightExpectedAmountOut: bigint;
  version: number;
  bump: number;
  authorityBump: number;
  settlementSignerBump: number;
  routingMode: number;
  beneficiaryCount: number;
  inflightKind: number;
  inflightDirection: number;
  settlementAuthority: Address;
  cumulativeRoutedBaseFees: bigint;
  cumulativeRoutedQuoteFees: bigint;
  reserved: ReadonlyUint8Array;
};

export type RehypeStateArgs = {
  launch: Address;
  baseMint: Address;
  quoteMint: Address;
  hookProgram: Address;
  buybackDestination: Address;
  feeRouting: FeeRoutingMatrixArgs;
  beneficiaries: Array<RehypeBeneficiaryArgs>;
  cumulativeBeneficiaryBase: number | bigint;
  cumulativeBeneficiaryQuote: number | bigint;
  distributedBaseByBeneficiary: Array<number | bigint>;
  distributedQuoteByBeneficiary: Array<number | bigint>;
  pendingCrossBase: number | bigint;
  pendingCrossQuote: number | bigint;
  pendingLpBase: number | bigint;
  pendingLpQuote: number | bigint;
  inflightAmountIn: number | bigint;
  inflightExpectedAmountOut: number | bigint;
  version: number;
  bump: number;
  authorityBump: number;
  settlementSignerBump: number;
  routingMode: number;
  beneficiaryCount: number;
  inflightKind: number;
  inflightDirection: number;
  settlementAuthority: Address;
  cumulativeRoutedBaseFees: number | bigint;
  cumulativeRoutedQuoteFees: number | bigint;
  reserved: ReadonlyUint8Array;
};

/** Gets the encoder for {@link RehypeStateArgs} account data. */
export function getRehypeStateEncoder(): FixedSizeEncoder<RehypeStateArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['launch', getAddressEncoder()],
      ['baseMint', getAddressEncoder()],
      ['quoteMint', getAddressEncoder()],
      ['hookProgram', getAddressEncoder()],
      ['buybackDestination', getAddressEncoder()],
      ['feeRouting', getFeeRoutingMatrixEncoder()],
      [
        'beneficiaries',
        getArrayEncoder(getRehypeBeneficiaryEncoder(), { size: 8 }),
      ],
      ['cumulativeBeneficiaryBase', getU64Encoder()],
      ['cumulativeBeneficiaryQuote', getU64Encoder()],
      [
        'distributedBaseByBeneficiary',
        getArrayEncoder(getU64Encoder(), { size: 8 }),
      ],
      [
        'distributedQuoteByBeneficiary',
        getArrayEncoder(getU64Encoder(), { size: 8 }),
      ],
      ['pendingCrossBase', getU64Encoder()],
      ['pendingCrossQuote', getU64Encoder()],
      ['pendingLpBase', getU64Encoder()],
      ['pendingLpQuote', getU64Encoder()],
      ['inflightAmountIn', getU64Encoder()],
      ['inflightExpectedAmountOut', getU64Encoder()],
      ['version', getU8Encoder()],
      ['bump', getU8Encoder()],
      ['authorityBump', getU8Encoder()],
      ['settlementSignerBump', getU8Encoder()],
      ['routingMode', getU8Encoder()],
      ['beneficiaryCount', getU8Encoder()],
      ['inflightKind', getU8Encoder()],
      ['inflightDirection', getU8Encoder()],
      ['settlementAuthority', getAddressEncoder()],
      ['cumulativeRoutedBaseFees', getU64Encoder()],
      ['cumulativeRoutedQuoteFees', getU64Encoder()],
      ['reserved', fixEncoderSize(getBytesEncoder(), 24)],
    ]),
    (value) => ({ ...value, discriminator: REHYPE_STATE_DISCRIMINATOR }),
  );
}

/** Gets the decoder for {@link RehypeState} account data. */
export function getRehypeStateDecoder(): FixedSizeDecoder<RehypeState> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['launch', getAddressDecoder()],
    ['baseMint', getAddressDecoder()],
    ['quoteMint', getAddressDecoder()],
    ['hookProgram', getAddressDecoder()],
    ['buybackDestination', getAddressDecoder()],
    ['feeRouting', getFeeRoutingMatrixDecoder()],
    [
      'beneficiaries',
      getArrayDecoder(getRehypeBeneficiaryDecoder(), { size: 8 }),
    ],
    ['cumulativeBeneficiaryBase', getU64Decoder()],
    ['cumulativeBeneficiaryQuote', getU64Decoder()],
    [
      'distributedBaseByBeneficiary',
      getArrayDecoder(getU64Decoder(), { size: 8 }),
    ],
    [
      'distributedQuoteByBeneficiary',
      getArrayDecoder(getU64Decoder(), { size: 8 }),
    ],
    ['pendingCrossBase', getU64Decoder()],
    ['pendingCrossQuote', getU64Decoder()],
    ['pendingLpBase', getU64Decoder()],
    ['pendingLpQuote', getU64Decoder()],
    ['inflightAmountIn', getU64Decoder()],
    ['inflightExpectedAmountOut', getU64Decoder()],
    ['version', getU8Decoder()],
    ['bump', getU8Decoder()],
    ['authorityBump', getU8Decoder()],
    ['settlementSignerBump', getU8Decoder()],
    ['routingMode', getU8Decoder()],
    ['beneficiaryCount', getU8Decoder()],
    ['inflightKind', getU8Decoder()],
    ['inflightDirection', getU8Decoder()],
    ['settlementAuthority', getAddressDecoder()],
    ['cumulativeRoutedBaseFees', getU64Decoder()],
    ['cumulativeRoutedQuoteFees', getU64Decoder()],
    ['reserved', fixDecoderSize(getBytesDecoder(), 24)],
  ]);
}

/** Gets the codec for {@link RehypeState} account data. */
export function getRehypeStateCodec(): FixedSizeCodec<
  RehypeStateArgs,
  RehypeState
> {
  return combineCodec(getRehypeStateEncoder(), getRehypeStateDecoder());
}

export function decodeRehypeState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>,
): Account<RehypeState, TAddress>;
export function decodeRehypeState<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>,
): MaybeAccount<RehypeState, TAddress>;
export function decodeRehypeState<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>,
): Account<RehypeState, TAddress> | MaybeAccount<RehypeState, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getRehypeStateDecoder(),
  );
}

export async function fetchRehypeState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<Account<RehypeState, TAddress>> {
  const maybeAccount = await fetchMaybeRehypeState(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeRehypeState<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig,
): Promise<MaybeAccount<RehypeState, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeRehypeState(maybeAccount);
}

export async function fetchAllRehypeState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<Account<RehypeState>[]> {
  const maybeAccounts = await fetchAllMaybeRehypeState(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeRehypeState(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig,
): Promise<MaybeAccount<RehypeState>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeRehypeState(maybeAccount));
}

export function getRehypeStateSize(): number {
  return 776;
}
